import { Link } from "react-router-dom";
import BookCover from "@/components/BookCover";
import EmailCapture from "@/components/EmailCapture";
import { EditorialMarquee } from "@/components/EditorialMarquee";
import { HeroLine, Reveal } from "@/components/Motion";
import { AMAZON_URL, KINDLE_URL } from "@/lib/site";

const forYou = [
  "You have read the spiritual books, attended the retreats, and quietly begun to suspect that none of it is working",
  "You are tired of memoirs that end in tidy resolution",
  "You want to read about a real life, not a curated one",
  "You are interested in what Indian philosophical traditions actually offer when they are not packaged for export",
];

const notForYou = [
  "You are looking for step-by-step instructions, exercises, or a programme",
  "You prefer your spiritual reading to confirm what you already believe",
  "You want a fast read",
];

export default function Home() {
  return (
    <>
      <section
        data-testid="hero-section"
        className="relative min-h-[calc(100svh-6rem)] flex items-center py-12 md:py-20 overflow-hidden"
        style={{ backgroundColor: "var(--paper)" }}
      >
        <div className="max-w-[1400px] w-full mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20 items-center">
          <div className="lg:col-span-7 order-1">
            <div className="hero-line-mask">
              <p data-testid="hero-overline" className="overline mb-6 animate-overline">
                A Memoir by Rahul Bhaskar
              </p>
            </div>
            <h1
              data-testid="hero-title"
              className="font-serif-lit text-5xl sm:text-6xl leading-[0.98]"
              style={{ color: "var(--ink)", fontWeight: 500 }}
            >
              <HeroLine delay={0.12}>Nothing in</HeroLine>
              <HeroLine delay={0.24} className="italic">the Way</HeroLine>
            </h1>
            <p
              data-testid="hero-tagline"
              className="font-serif-lit italic text-xl md:text-2xl leading-relaxed mt-8 max-w-xl hero-copy-reveal"
              style={{ color: "var(--ink-soft)" }}
            >
              The long search for something he could not name — and what he
              found when the searching stopped.
            </p>

            <div
              data-testid="hero-buy-buttons"
              className="mt-10 flex flex-wrap items-center gap-4 hero-copy-reveal hero-copy-delay"
            >
              <a
                href={AMAZON_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="hero-buy-amazon"
                className="btn-ink"
              >
                Buy on Amazon
              </a>
              <a
                href={KINDLE_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="hero-buy-kindle"
                className="btn-ghost"
              >
                Buy on Kindle
              </a>
              <Link
                to="/book"
                data-testid="hero-book-link"
                className="literary-link font-nav uppercase tracking-[0.22em] text-[0.74rem] ml-1"
                style={{ color: "var(--ink)" }}
              >
                The Book →
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5 flex justify-center lg:justify-end order-2 hero-cover-reveal">
            <BookCover size="md" testId="hero-book-cover" interactive />
          </div>
        </div>
      </section>

      <section
        data-testid="pitch-section"
        className="py-24 md:py-32"
        style={{ backgroundColor: "var(--paper-deep)" }}
      >
        <div className="max-w-[980px] mx-auto px-6 md:px-12">
          <Reveal>
            <p className="overline mb-12">The 60-second pitch</p>
          </Reveal>
          <div className="manifesto-list">
            {[
              <>For most of his life, Rahul Bhaskar was looking in the wrong place.</>,
              <>He did not know it was the wrong place. He only knew that something he could not name was missing — and that the missing thing was not the kind of thing his career, his marriage, or his good fortune was equipped to repair.</>,
              <>So he searched. Christianity in his early adulthood. Buddhism and Vipassana through his thirties. Quieter traditions in between. Each one promised an answer; each one, eventually, did not hold.</>,
              <><em className="italic">Nothing in the Way</em> is the record of those years — the seeking, the cost of the seeking, and the slow, inconvenient recognition that what he had been looking for had been present the whole time. What stood in the way was the looking.</>,
              <>This is not a book about arrival. It is a book about how arrival happens to people who are no longer trying to arrive.</>,
            ].map((paragraph, index) => (
              <Reveal
                key={index}
                className="manifesto-row"
                delay={Math.min(index * 0.06, 0.24)}
                testId={`pitch-chapter-${index + 1}`}
              >
                <span className="manifesto-number">{String(index + 1).padStart(2, "0")}</span>
                <p className="font-serif-lit text-xl md:text-2xl leading-[1.62]">{paragraph}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <EditorialMarquee />

      <section
        data-testid="for-readers-section"
        className="py-24 md:py-32"
        style={{ backgroundColor: "var(--paper)" }}
      >
        <div className="max-w-[1100px] mx-auto px-6 md:px-12">
          <Reveal><p className="overline mb-10">For readers who</p></Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 md:gap-24">
            <Reveal>
              <h2 className="font-serif-lit text-2xl md:text-3xl mb-8" style={{ fontWeight: 500 }}>
                This book is for you if:
              </h2>
              <ul data-testid="for-you-list" className="font-serif-lit text-lg leading-relaxed space-y-5">
                {forYou.map((text, index) => (
                  <li key={text} className="flex gap-4">
                    <span className="list-number">0{index + 1}</span>
                    <span>{text}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-serif-lit text-2xl md:text-3xl mb-8" style={{ fontWeight: 500 }}>
                This book is probably not for you if:
              </h2>
              <ul data-testid="not-for-you-list" className="font-serif-lit text-lg leading-relaxed space-y-5" style={{ color: "var(--ink-soft)" }}>
                {notForYou.map((text, index) => (
                  <li key={text} className="flex gap-4">
                    <span className="list-number">0{index + 1}</span>
                    <span>{text}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
          <Reveal>
            <p className="font-serif-lit italic text-base mt-14 max-w-2xl" style={{ color: "var(--ink-soft)" }}>
              The honesty of the second list is intentional. The book respects readers enough to tell them when it is not for them.
            </p>
          </Reveal>
        </div>
      </section>

      <section
        data-testid="about-short-section"
        className="py-24 md:py-32 border-y"
        style={{ backgroundColor: "var(--paper-deep)", borderColor: "var(--rule)" }}
      >
        <div className="max-w-[1100px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20">
          <Reveal className="md:col-span-3">
            <p className="overline">About the author</p>
          </Reveal>
          <Reveal className="md:col-span-9" delay={0.08}>
            <h2 data-testid="about-short-title" className="font-serif-lit text-4xl sm:text-5xl mb-8" style={{ fontWeight: 500 }}>
              Rahul Bhaskar
            </h2>
            <p className="font-serif-lit text-xl md:text-2xl leading-[1.65] max-w-3xl">
              Rahul Bhaskar spent most of his life looking in the wrong place. He searched across traditions — Christianity, Buddhism, Vipassana — before encountering the teaching that finally held. <em className="italic">Nothing in the Way</em> is his account of that long search, what it cost him, and what he found when the searching stopped.
            </p>
            <p className="font-serif-lit text-lg md:text-xl leading-relaxed mt-6 max-w-2xl" style={{ color: "var(--ink-soft)" }}>
              He lives in California with his family. <em className="italic">Nothing in the Way</em> is his first book.
            </p>
            <Link to="/about" data-testid="about-short-cta" className="literary-link overline mt-10 inline-block" style={{ color: "var(--ink)" }}>
              Read more about Rahul →
            </Link>
          </Reveal>
        </div>
      </section>

      <EmailCapture testIdPrefix="home-newsletter" />
    </>
  );
}